import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import { isInteger, isNil } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { FormattedFloatOptionalField } from "../../commons/form-fields/FormattedFloatOptionalField.js";
import { PropValues, TemplateTypePropValues } from "./properties.js";

export type NumberRangeSectionFormValues = {
  setRange: boolean;
  setStep: boolean;
  range: {
    min: number | null;
    max: number | null;
  };
  step: number | null;
};

interface Props {
  disabled?: boolean;
  disabledSetRange?: boolean;
  disabledSetStep?: boolean;
}

export const NumberRangeSection = ({
  disabled,
  disabledSetRange,
  disabledSetStep,
}: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const formik = useFormikContext<NumberRangeSectionFormValues>();
  return (
    <Stack spacing={1}>
      <CheckboxField
        name={"setRange"}
        label={t("Set min and/or max", {
          ns: "QuoteItem",
        })}
        disabled={disabled || disabledSetRange || formik.isSubmitting}
      />
      {formik.values.setRange && (
        <Stack direction={"row"} spacing={1} paddingLeft={4} flex={1}>
          <FormattedFloatOptionalField
            name={"range.min"}
            label={t("Min", { ns: "QuoteItem" })}
          />
          <FormattedFloatOptionalField
            name={"range.max"}
            label={t("Max", { ns: "QuoteItem" })}
          />
        </Stack>
      )}
      <CheckboxField
        name={"setStep"}
        label={t("Set step", { ns: "QuoteItem" })}
        disabled={disabled || disabledSetStep || formik.isSubmitting}
      />
      {formik.values.setStep && (
        <Stack direction={"row"} spacing={1} paddingLeft={4} flex={1}>
          <FormattedFloatOptionalField
            name={"step"}
            label={t("Step", { ns: "QuoteItem" })}
          />
        </Stack>
      )}
    </Stack>
  );
};

export const useNumberRangeValidationSchema = (property: PropValues | null) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  return React.useMemo(() => {
    if (!property) {
      // on creation
      return {
        setRange: Yup.boolean(),
        setStep: Yup.boolean(),
        range: Yup.object()
          .shape({
            min: Yup.number().nullable(),
            max: Yup.number().nullable(),
          })
          .when("setRange", {
            is: true,
            then: Yup.object().shape({
              min: Yup.number().nullable(),
              max: Yup.number()
                .nullable()
                .when("min", (min: number | null, schema: any) => {
                  return schema
                    .test({
                      test: (max: number | null) => {
                        return !(min === null && max === null);
                      },
                      message: t("Min or max should be defined", {
                        ns: "Global",
                      }),
                    })
                    .test({
                      test: (max: number | null) =>
                        !(min !== null && max !== null && min > max),
                      message: t("Max should be greater or equal than min", {
                        ns: "Global",
                      }),
                    });
                }),
            }),
          }),
        step: Yup.number()
          .label(t("Step", { ns: "QuoteItem" }))
          .nullable()
          .when("setStep", {
            is: true,
            then: schema => schema.required().moreThan(0),
          }),
      };
    } else {
      // on editing
      return {
        setRange: Yup.boolean(),
        setStep: Yup.boolean(),
        range: Yup.object()
          .shape({
            min: Yup.number()
              .label(t("Min", { ns: "QuoteItem" }))
              .nullable(),
            max: Yup.number()
              .label(t("Max", { ns: "QuoteItem" }))
              .nullable(),
          })
          .when("setRange", {
            is: true,
            then: Yup.object().shape({
              min: Yup.number()
                .label(t("Min", { ns: "QuoteItem" }))
                .nullable()
                .test({
                  test: min => {
                    if (!isNil(min)) {
                      if (
                        property.isNumber &&
                        !property.isArray &&
                        !isNil(property.valueNumber)
                      ) {
                        return property.valueNumber >= min;
                      } else if (
                        property.isNumber &&
                        property.isArray &&
                        !isNil(property.valueNumberArray)
                      ) {
                        return property.valueNumberArray.every(
                          valueNumber => valueNumber >= min
                        );
                      }
                    }

                    return true;
                  },
                  message: t(
                    "Min should be less or equal than the current value",
                    {
                      ns: "QuoteItem",
                      value: property.isNumber && property.isArray ? 2 : 1,
                    }
                  ),
                }),
              max: Yup.number()
                .label(t("Max", { ns: "QuoteItem" }))
                .nullable()
                .when("min", (min: number | null, schema: any) => {
                  return schema
                    .test({
                      test: (max: number | null) => {
                        return !(min === null && max === null);
                      },
                      message: t("Min or max should be defined", {
                        ns: "Global",
                      }),
                    })
                    .test({
                      test: (max: number | null) =>
                        !(min !== null && max !== null && min > max),
                      message: t("Max should be greater or equal than min", {
                        ns: "Global",
                      }),
                    });
                })
                .test({
                  test: max => {
                    if (!isNil(max)) {
                      if (
                        property.isNumber &&
                        !property.isArray &&
                        !isNil(property.valueNumber)
                      ) {
                        return property.valueNumber <= max;
                      } else if (
                        property.isNumber &&
                        property.isArray &&
                        !isNil(property.valueNumberArray)
                      ) {
                        return property.valueNumberArray.every(
                          valueNumber => valueNumber <= max
                        );
                      }
                    }

                    return true;
                  },
                  message: t(
                    "Max should be greater or equal than the current value",
                    {
                      ns: "QuoteItem",
                      value: property.isNumber && property.isArray ? 2 : 1,
                    }
                  ),
                }),
            }),
          }),
        step: Yup.number()
          .label(t("Step", { ns: "QuoteItem" }))
          .nullable()
          .when("setStep", {
            is: true,
            then: schema =>
              schema
                .required()
                .moreThan(0)
                .when(
                  "range",
                  (
                    range: { min: number | null; max: number | null } | null,
                    schema: any
                  ) => {
                    return schema.test({
                      test: (step: number | null) => {
                        if (!isNil(step) && !isNil(range)) {
                          if (
                            property.isNumber &&
                            !property.isArray &&
                            !isNil(property.valueNumber)
                          ) {
                            if (!isNil(range.min)) {
                              const multiplier =
                                (property.valueNumber - range.min) / step;
                              return isInteger(multiplier) && multiplier >= 0;
                            } else if (!isNil(range.max)) {
                              const multiplier =
                                (range.max - property.valueNumber) / step;
                              return isInteger(multiplier) && multiplier >= 0;
                            }
                          } else if (
                            property.isNumber &&
                            property.isArray &&
                            !isNil(property.valueNumberArray)
                          ) {
                            if (!isNil(range.min)) {
                              const min = range.min;
                              return property.valueNumberArray.every(
                                valueNumber => {
                                  const multiplier = (valueNumber - min) / step;
                                  return (
                                    isInteger(multiplier) && multiplier >= 0
                                  );
                                }
                              );
                            } else if (!isNil(range.max)) {
                              const max = range.max;
                              return property.valueNumberArray.every(
                                valueNumber => {
                                  const multiplier = (max - valueNumber) / step;
                                  return (
                                    isInteger(multiplier) && multiplier >= 0
                                  );
                                }
                              );
                            }
                          }
                        }

                        return true;
                      },
                      message: t("Step must match current value", {
                        ns: "QuoteItem",
                        value: property.isNumber && property.isArray ? 2 : 1,
                      }),
                    });
                  }
                ),
          }),
      };
    }
  }, [property, t]);
};

export const useNumberRangeValidationSchemaWithTemplateType = (
  templateTypePropValues: TemplateTypePropValues
) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  return React.useMemo(() => {
    return {
      setRange: Yup.boolean(),
      setStep: Yup.boolean(),
      range: Yup.object()
        .shape({
          min: Yup.number()
            .label(t("Min", { ns: "QuoteItem" }))
            .nullable(),
          max: Yup.number()
            .label(t("Max", { ns: "QuoteItem" }))
            .nullable(),
        })
        .when("setRange", {
          is: true,
          then: Yup.object().shape({
            min: Yup.number()
              .label(t("Min", { ns: "QuoteItem" }))
              .nullable()
              .when([], {
                is: () => templateTypePropValues.rangeMin !== null,
                then: schema =>
                  schema.test({
                    test: (min: number | null | undefined) => {
                      return !(min === null || min === undefined);
                    },
                    message: t("Min should be defined", { ns: "Global" }),
                  }),
              })
              .when([], {
                is: () => templateTypePropValues.rangeMin !== null,
                then: schema => schema.min(templateTypePropValues.rangeMin!),
              }),
            max: Yup.number()
              .label(t("Max", { ns: "QuoteItem" }))
              .nullable()
              .when([], {
                is: () => templateTypePropValues.rangeMax !== null,
                then: schema =>
                  schema.test({
                    test: (max: number | null | undefined) => {
                      return !(max === null || max === undefined);
                    },
                    message: t("Max should be defined", { ns: "Global" }),
                  }),
              })
              .when([], {
                is: () => templateTypePropValues.rangeMax !== null,
                then: schema => schema.max(templateTypePropValues.rangeMax!),
              })
              .when("min", (min: number | null, schema: any) => {
                return schema
                  .test({
                    test: (max: number | null) => {
                      return !(min === null && max === null);
                    },
                    message: t("Min or max should be defined", {
                      ns: "Global",
                    }),
                  })
                  .test({
                    test: (max: number | null) =>
                      !(min !== null && max !== null && min > max),
                    message: t("Max should be greater or equal than min", {
                      ns: "Global",
                    }),
                  });
              }),
          }),
        }),
      step: Yup.number()
        .label(t("Step", { ns: "QuoteItem" }))
        .nullable()
        .when("setStep", {
          is: true,
          then: schema => schema.required().moreThan(0),
        }),
    };
  }, [t, templateTypePropValues.rangeMax, templateTypePropValues.rangeMin]);
};
